/**
 * authFehler.ts — Supabase-Auth-Fehlercodes in verständliche Texte.
 *
 * Supabase liefert Codes wie 'email_not_confirmed', 'invalid_credentials'
 * usw. Hier werden sie auf i18n-Schlüssel abgebildet, damit Nutzer nie den
 * englischen Originaltext sehen. Unbekannter Code → `authErrUnknown`
 * (Original nur in der Konsole). Ohne Code → `fallback`.
 *
 * Herausgelöst aus `useAuth` (17.10.2026), damit auch „Passwort vergessen →
 * neues Passwort" (`PasswordRecovery.tsx`) dieselben Texte zeigt — dort
 * erschien bis dahin der englische Supabase-Text.
 *
 * Codes-Quelle: https://supabase.com/docs/guides/auth/debugging/error-codes
 * Stand: 04.05.2026 — bei Erweiterungen Liste prüfen.
 *
 * @module lib/authFehler
 */

import type { TranslateFn } from '../types.js';

/**
 * Übersetzt einen Supabase-Auth-Fehlercode.
 *
 * @param code     Maschinenlesbarer Code (kann fehlen, z. B. bei Netzwerkfehlern)
 * @param fallback Text, wenn kein Code vorliegt
 * @param t        Übersetzungsfunktion
 * @returns Text für die Anzeige
 */
export function authFehlerText(code: string | undefined, fallback: string, t: TranslateFn): string {
  if (!code) return fallback;
  switch (code) {
    // ─── Login-Probleme ────────────────────────────────────────
    case 'invalid_credentials':
    case 'invalid_grant':
      return t('authErrInvalidCredentials');
    case 'email_not_confirmed':
      return t('authErrEmailNotConfirmed');
    case 'user_banned':
      return t('authErrUserBanned');

    // ─── Registrierung-Probleme ────────────────────────────────
    case 'user_already_exists':
    case 'email_exists':
      return t('authErrUserExists');
    case 'weak_password':
      return t('authErrWeakPassword');
    case 'signup_disabled':
    case 'email_provider_disabled':
      return t('authErrEmailProviderDisabled');
    case 'email_address_invalid':
      return t('authErrEmailInvalid');

    // ─── Passwort ändern ───────────────────────────────────────
    case 'same_password':
      return t('authErrSamePassword');

    // ─── Session ───────────────────────────────────────────────
    case 'session_expired':
    case 'session_not_found':
    case 'refresh_token_already_used':
    case 'refresh_token_not_found':
      return t('authErrSessionExpired');

    // ─── Rate-Limits ───────────────────────────────────────────
    case 'over_email_send_rate_limit':
    case 'over_request_rate_limit':
    case 'over_sms_send_rate_limit':
      return t('authErrRateLimit');

    // ─── Sicherheit / Validierung ──────────────────────────────
    case 'captcha_failed':
      return t('authErrCaptcha');
    case 'validation_failed':
    case 'bad_json':
      return t('authErrValidation');

    // ─── Server-Fehler ─────────────────────────────────────────
    case 'unexpected_failure':
    case 'request_timeout':
      return t('authErrServerError');

    // ─── Unbekannter Code ──────────────────────────────────────
    // Original-Message für Debug ins Log, User bekommt generischen Text.
    default:
      // eslint-disable-next-line no-console
      console.warn('[mapAuthError] Unmapped Supabase auth code:', code, '— fallback message:', fallback);
      return t('authErrUnknown');
  }
}
